import React from 'react';
import { useLang } from '../../i18n/LanguageContext';
import { guidePreviewText } from '../../i18n/pages/guidePreview';
import { GuideSampleField, isUneditedSample, sampleFor } from './guideSamples';

interface Props {
  field: GuideSampleField;
  value: string | null | undefined;
  onChange: (text: string) => void;
}

/**
 * Sits under a Guide textarea. An empty field offers the sample; a field still holding an untouched
 * sample says so in words (not colour alone) and offers to remove it; a field the host has written
 * in shows nothing. It never appears over existing text, so inserting cannot overwrite anything.
 */
const GuideSampleControl: React.FC<Props> = ({ field, value, onChange }) => {
  const { lang } = useLang();
  const p = guidePreviewText[lang];
  const blank = !value || !value.trim();
  const sample = !blank && isUneditedSample(field, value);
  if (!blank && !sample) return null;

  const btn: React.CSSProperties = {
    background: 'none', border: 'none', padding: 0, minHeight: 32, cursor: 'pointer',
    fontSize: '0.82rem', fontWeight: 600,
  };

  if (blank) {
    return (
      <div style={{ marginTop: 6 }}>
        <button type="button" data-testid={`guide-sample-insert-${field}`} style={{ ...btn, color: '#2563eb' }}
          onClick={() => onChange(sampleFor(lang, field))}>
          ✨ {p.sampleInsert}
        </button>
      </div>
    );
  }
  return (
    <div style={{ marginTop: 6, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
      <span role="note" style={{ fontSize: '0.78rem', fontWeight: 700, color: '#92400e', background: '#fef3c7', border: '1px solid #fcd34d', borderRadius: 6, padding: '2px 8px' }}>
        {p.sampleBadge}
      </span>
      <button type="button" data-testid={`guide-sample-remove-${field}`} style={{ ...btn, color: '#dc2626' }}
        onClick={() => onChange('')}>
        {p.sampleRemove}
      </button>
      <span style={{ flexBasis: '100%', fontSize: '0.78rem', color: '#64748b' }}>{p.sampleHelp}</span>
    </div>
  );
};

export default GuideSampleControl;
